import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, IconButton, Link, Typography } from '@mui/material'
import { FileDownloadOutlined, ZoomIn, ZoomOut } from '@mui/icons-material'
import { Document, Page, pdfjs } from 'react-pdf'
import { useIntl } from 'react-intl'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'

// Legacy build keeps older WebViews (iOS < 17.4) working; the version always
// matches the API since both come from the same pdfjs-dist install.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

// isEvalSupported hardens against CVE-2024-4367-style font eval issues
const DOCUMENT_OPTIONS = { isEvalSupported: false }

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.25
const SCROLL_END_THRESHOLD_PX = 24

interface PdfViewerProps {
  fileUrl: string
  height?: number | string
  /**
   * Fired once when the user reaches the end of the document (or immediately
   * when the whole document fits without scrolling / has a single page).
   */
  onScrolledToEnd?: () => void
}

const PdfViewer: FC<PdfViewerProps> = ({ fileUrl, height = '750px', onScrolledToEnd }) => {
  const { formatMessage } = useIntl()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [scale, setScale] = useState(1)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  const scrollBoxRef = useRef<HTMLDivElement | null>(null)
  const scrolledToEndRef = useRef(false)
  const renderedPagesRef = useRef(0)

  useEffect(() => {
    scrolledToEndRef.current = false
    renderedPagesRef.current = 0
    setNumPages(null)
    setCurrentPage(1)
  }, [fileUrl])

  useEffect(() => {
    const element = scrollBoxRef.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setContainerWidth(width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const fireScrolledToEnd = useCallback(() => {
    if (scrolledToEndRef.current) return
    scrolledToEndRef.current = true
    onScrolledToEnd?.()
  }, [onScrolledToEnd])

  const handleDocumentLoadSuccess = useCallback(
    ({ numPages: loadedPages }: { numPages: number }) => {
      setNumPages(loadedPages)
      if (loadedPages <= 1) {
        fireScrolledToEnd()
      }
    },
    [fireScrolledToEnd]
  )

  const handlePageRenderSuccess = useCallback(() => {
    renderedPagesRef.current += 1
    const element = scrollBoxRef.current
    if (!element || renderedPagesRef.current < (numPages ?? Infinity)) return
    if (element.scrollHeight <= element.clientHeight + SCROLL_END_THRESHOLD_PX) {
      fireScrolledToEnd()
    }
  }, [fireScrolledToEnd, numPages])

  const handleScroll = useCallback(() => {
    const element = scrollBoxRef.current
    if (!element || !numPages) return
    const { scrollTop, clientHeight, scrollHeight } = element
    const pageHeight = scrollHeight / numPages
    const visiblePage = Math.min(
      numPages,
      Math.floor((scrollTop + clientHeight / 2) / pageHeight) + 1
    )
    setCurrentPage(visiblePage)
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX) {
      fireScrolledToEnd()
    }
  }, [fireScrolledToEnd, numPages])

  const pageWidth = useMemo(
    () => (containerWidth ? Math.floor(containerWidth * scale) : undefined),
    [containerWidth, scale]
  )

  const loadingIndicator = (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  )

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size='small'
            disabled={scale <= MIN_SCALE}
            onClick={() => setScale((value) => Math.max(MIN_SCALE, value - SCALE_STEP))}
            aria-label={formatMessage({ id: 'pdfViewer.zoomOut', defaultMessage: 'Pomniejsz' })}
          >
            <ZoomOut fontSize='small' />
          </IconButton>
          <IconButton
            size='small'
            disabled={scale >= MAX_SCALE}
            onClick={() => setScale((value) => Math.min(MAX_SCALE, value + SCALE_STEP))}
            aria-label={formatMessage({ id: 'pdfViewer.zoomIn', defaultMessage: 'Powiększ' })}
          >
            <ZoomIn fontSize='small' />
          </IconButton>
        </Box>
        {numPages && (
          <Typography variant='caption' color='text.secondary'>
            {formatMessage(
              { id: 'pdfViewer.pageIndicator', defaultMessage: '{current} / {total}' },
              { current: currentPage, total: numPages }
            )}
          </Typography>
        )}
        <IconButton
          size='small'
          component={Link}
          href={fileUrl}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={formatMessage({ id: 'pdfViewer.download', defaultMessage: 'Pobierz' })}
        >
          <FileDownloadOutlined fontSize='small' />
        </IconButton>
      </Box>
      <Box
        ref={scrollBoxRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'grey.100',
          '.react-pdf__Page': {
            mx: 'auto',
            my: 1,
            width: 'fit-content',
            boxShadow: 1
          }
        }}
      >
        <Document
          file={fileUrl}
          options={DOCUMENT_OPTIONS}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading={loadingIndicator}
          error={
            <Alert severity='error' sx={{ m: 2 }}>
              {formatMessage({
                id: 'pdfViewer.loadError',
                defaultMessage: 'Nie udało się wczytać dokumentu. Spróbuj ponownie.'
              })}
            </Alert>
          }
        >
          {Array.from({ length: numPages ?? 0 }, (_, index) => (
            <Page
              key={index + 1}
              pageNumber={index + 1}
              width={pageWidth}
              onRenderSuccess={handlePageRenderSuccess}
              loading=''
            />
          ))}
        </Document>
      </Box>
    </Box>
  )
}

export default PdfViewer
